import React from 'react';
import styled, {css} from 'styled-components';

import {connectState} from 'web-app/util/connect-state';
import Carousel, {isComplexCarouselItem, type CarouselItem} from 'web-app/react/components/carousel/carousel';
import {Subheader, Caption} from 'web-app/react/components/text/text';
import {Flex} from 'web-app/react/components/layout/layout';
import NewLookTheme from 'web-app/styleguide/themes/new-look';
import {media} from 'web-app/styleguide/utils';
import {hexToRGBString} from 'web-app/util/hex-to-rgb';
import AddPerson from 'web-app/react/components/icons/legacy/lazy-icons/add-person';

import {type Account} from './models';
import {type LoginStateProps} from './reducer';
import * as Selectors from './selectors';
import * as Constants from './constants';

interface InputProps {
    onAccountSelect: (loginId: Account['loginId']) => void;
}
interface StateProps {
    selectedAccountId: LoginStateProps['selectedAccountId'];
    accountsForCarousel: CarouselItem[] | null;
}
type Props = InputProps & StateProps;
class AccountSelector extends React.PureComponent<Props> {
    private static CarouselSettings = {
        className: 'slick-carousel',
        centerMode: true,
        centerPadding: '0px',
        focusOnSelect: true,
        infinite: false,
        variableWidth: true,
        touchThreshold: 200,
        speed: 250,
        edgeFriction: 0.5,
        swipeToSlide: true,
        arrows: false,
    };
    constructor(props) {
        super(props);
        this.handleChange = this.handleChange.bind(this);
    }
    public render() {
        if (!this.props.accountsForCarousel) {
            return null;
        }
        return (
            <CarouselContainer>
                <Carousel
                    onChange={this.handleChange}
                    items={this.props.accountsForCarousel}
                    initial={this.props.selectedAccountId}
                    defaultIndexOrId={this.props.selectedAccountId}
                    renderer={carouselItemRenderer}
                    arrowFill={NewLookTheme.text}
                    arrowSize={48}
                    settings={AccountSelector.CarouselSettings}
                />
            </CarouselContainer>
        );
    }
    private handleChange(idOrIndex: Account['loginId'] | number) {
        if (typeof idOrIndex === 'string') {
            this.props.onAccountSelect(idOrIndex);
        }
    }
}

export default connectState<InputProps, StateProps, React.ComponentType<InputProps & StateProps>>(state => ({
    selectedAccountId: state.login.selectedAccountId,
    accountsForCarousel: Selectors.accountsForCarousel(state),
}))(AccountSelector);

const CarouselContainer = styled.div`
    width: 100%;
    margin-bottom: 30px;
    .slick-slide {
        perspective: 200px;
        perspective-origin: 50% 50%;
        > div {
            transition: transform 0.2s ${props => props.theme.timings.functions.default};
            transform: scale3d(0.75, 0.75, 0.75);
        }
    }
    .slick-active {
        > div {
            transform: scale3d(1, 1, 1);
        }
    }
    .slick-slider {
        margin-bottom: 0;
    }

    ${media.mobile`&& {
        height: auto;
        margin-bottom: 12px;
    }`}
    ${media.portrait`
        margin-bottom: 3.5vh;
    `}
    ${media.tablet`
        @media screen and (min-aspect-ratio: 1024/530) {
            margin-bottom: 15px;
        }
    `}
    ${media.notLandscape`
        @media (max-height: 600px) {
            margin-bottom: 8px;
        }
    `}
`;

const carouselItemRenderer = (item: CarouselItem) => {
    if (!isComplexCarouselItem(item)) {
        return null;
    }

    if (item.id === Constants.NEW_ACCOUNT_IDENTIFIER) {
        return (
            <CarouselItemContainer direction="column" align="center">
                <NewAccountElement id="addNewLogin">
                    <NewUserImage />
                </NewAccountElement>
                <Subheader emphasized>{item.title}</Subheader>
            </CarouselItemContainer>
        );
    }

    return (
        <CarouselItemContainer direction="column" align="center">
            {typeof item.image === 'string' && <CarouselImage src={item.image} />}
            {/* Very long names break the UI, so we truncate them with an ellipsis.
                The max-width is set to 275px as it's slightly less than the width of LoginContainer,
                and just allows the next element to be seen off to the side on a smaller screen.*/}
            <Subheader emphasized ellipsis maxWidth="275px">
                {item.title}
            </Subheader>
            {item.subtitle && <Caption secondary>{item.subtitle}</Caption>}
        </CarouselItemContainer>
    );
};

const CarouselItemContainer = styled(Flex)`
    cursor: pointer;
`;

const NewUserImage = styled(AddPerson)`
    height: 45px;
    width: 45px;
    transform: translate(3px, 2px);
    ${media.mobile`
        height: 45%;
        width: 45%;
        transform: translate(5%, 5%);
    `}
    ${media.tablet`
        transform: translate(3%, 3%);
    `}
    ${media.notLandscape`
        @media (max-height: 600px) {
            height: 25px;
            width: 25px;
            transform: translate(2px, 0px);
        }
    `}
`;

const SharedCarouselElementCSS = css`
    height: 100px;
    width: 100px;
    border-radius: 50%;
    margin: 0 16px 16px 16px;
    box-shadow: 0 4px 8px 0 rgba(${props => hexToRGBString(props.theme.text)}, 0.1);
    ${media.mobile`
        height: 85px;
        width: 85px;
        margin: 0 8px 8px 8px;
    `}
    ${media.tablet`
        @media screen and (min-aspect-ratio: 1024/530) {
            height: 65px;
            width: 65px;
            margin: 0 6px 6px 6px;
        }
    `}
    ${media.notLandscape`
        @media (max-height: 600px) {
            height: 55px;
            width: 55px;
        }
    `}
`;

const NewAccountElement = styled.div`
    background: ${props => props.theme.invertedText};
    font-size: 50px;
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${props => props.theme.text};
    border: 1px solid ${props => props.theme.delimiter};
    ${SharedCarouselElementCSS}
`;
const CarouselImage = styled.img`
    && {
        ${SharedCarouselElementCSS}
    }
`;
